import qs from "qs"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { Breadcrumbs } from "~/ui-rtk/components/features/dashboards/BrandMediaOptimizationDashboard/components/Breadcrumbs/index"
import useConnect, {
  CREATIVE_REPORT,
  CREATIVE_REPORT_OPTIONS,
  CREATIVE_REPORTS_METRICS_MAP,
  DEFAULT_METRICS,
  ALWAYS_METRICS,
  type TCreativeReport,
  type TMetricsMapKey,
  ITEMS_PER_PAGE,
  MONETARY_METRICS,
  COEF_METRICS,
  PERCENTAGE_METRICS,
  BRAND_METRICS,
  REVENUE_METRICS,
  ENGAGEMENT_METRICS,
  PERFORMANCE_METRICS,
} from "./connect"
import { useMemo, useState, useEffect, useRef } from "react"
import DropdownMenu from "~/ui-rtk/components/ui/controls/DropdownMenu/DropdownMenu"
import {
  BoltIcon,
  ChevronDownIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import { DateRangePicker } from "~/ui-rtk/components/ui/controls"
import useDate from "~/ui-rtk/hooks/date"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { Card, Loader, RichTooltip } from "~/ui-rtk/components/ui/common"
import CreativeReportCard from "./components/CreativeReportCard"
import {
  BlastWizard,
  type BlastSource,
} from "~/ui-rtk/components/features/blast-wizard"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import type { TableVisualizationColumnFilter } from "~/ui-rtk/constants/table-visualization-types"
import useSelectFilterOptions from "./hooks/useSelectFilterOptions"
import type { TManualFilter } from "~/ui-rtk/components/ui/charts/TableVisualization/constants"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import useUrlSorting, { TSortingState } from "~/ui-rtk/hooks/url-sorting"
import SortByControl from "~/ui-rtk/components/ui/controls/SortBy/SortBy"
import useDrawer from "~/ui-rtk/hooks/drawer"
import { DRAWER_TYPE, TDrawerConfig } from "~/ui-rtk/constants/drawer"
import MultiSelectWithChips, {
  TMultiSelectWithChipsOption,
  TMultiSelectWithChipsOptionGroup,
} from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import AmazonToggle from "~/ui-rtk/components/ui/controls/AmazonToggle/AmazonToggle"
import { Toggle } from "~/ui-rtk/components/ui/controls/Toggle"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import { useResolveAssetsMutation } from "~/ui-rtk/api/assetsApi"
import { useGetMetaCreativeMediaMutation } from "~/ui-rtk/api/paidMediaApi"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { cacheAssetInfo } from "~/ui-rtk/app/slices/asset-cache.slice"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import type { AssetSourceType } from "~/ui-rtk/components/atoms/MarathonImage"

const MODE_URL_KEY = "report-mode"
const METRICS_URL_KEY = "metrics"
// When in Root Ad Name mode, controls whether cards are grouped by media
// channel. Absent = aggregated across all channels (the default); "true" opts
// into per-channel cards.
const GROUP_BY_CHANNEL_URL_KEY = "group_by_channel"

// Quick-select metric presets ("shortcuts") for common reporting views.
// ALWAYS_METRICS are merged in on top of every preset. A preset may also set
// a default sort and default column filters applied when it is selected.
const METRIC_PRESETS: {
  label: string
  metrics: TMetricsMapKey[]
  defaultSort?: TSortingState
  defaultFilters?: TableVisualizationColumnFilter[]
}[] = [
  {
    label: "TOF",
    metrics: [
      "spend",
      "brand_value_over_spend",
      "estimated_d180_brand_value",
      "follows",
      "shares",
      "comments",
    ],
    defaultSort: { id: "spend", desc: true },
    defaultFilters: [
      {
        id: "initiative_type",
        variant: "text",
        operator: "contains",
        value: "generation",
      },
    ],
  },
  {
    label: "DR",
    metrics: [
      "spend",
      "modeled_revenue",
      "purchases",
      "purchase_value_over_spend",
      "total_value",
    ],
    defaultSort: { id: "spend", desc: true },
    defaultFilters: [
      {
        id: "initiative_type",
        variant: "text",
        operator: "contains",
        value: "capture",
      },
    ],
  },
  {
    label: "Engagement",
    metrics: [
      "spend",
      "reach",
      "likes",
      "comments",
      "shares",
      "saves",
      "follows",
    ],
  },
  {
    label: "Brand Value",
    metrics: [
      "spend",
      "brand_value_over_spend",
      "d30_brand_value_agg",
      "d60_brand_value_agg",
      "d90_brand_value_agg",
      "estimated_d180_brand_value",
    ],
  },
  {
    label: "Attributed Revenue",
    metrics: [
      "spend",
      "purchase_value",
      "ga4_total_revenue",
      "shopify_first_total_revenue",
      "shopify_last_total_revenue",
      "ga4_total_revenue_over_spend",
    ],
  },
  {
    label: "Coverage",
    metrics: [
      "spend",
      "distinct_ad_count",
      "distinct_adset_count",
      "distinct_campaign_count",
      "active_ads",
      "active_adsets",
      "active_campaigns",
      "adset_coverage",
    ],
  },
]

// Filter ids managed by presets: applying any preset replaces filters on
// these ids, so switching e.g. TOF → Engagement clears TOF's initiative
// filter instead of leaving it behind.
const PRESET_FILTER_IDS = new Set(
  METRIC_PRESETS.flatMap(({ defaultFilters }) =>
    (defaultFilters ?? []).map(({ id }) => id),
  ),
)

// Define metric groups
const METRIC_GROUPS: TMultiSelectWithChipsOptionGroup[] = [
  { label: "Brand Metrics", value: "brand" },
  { label: "Revenue Metrics", value: "revenue" },
  { label: "Platform Engagements", value: "engagements" },
  { label: "Performance Metrics", value: "performance" },
]

// Helper function to get group for a metric
const getMetricGroup = (metric: TMetricsMapKey): string => {
  if (BRAND_METRICS.includes(metric)) return "brand"
  if (REVENUE_METRICS.includes(metric)) return "revenue"
  if (ENGAGEMENT_METRICS.includes(metric)) return "engagements"
  if (PERFORMANCE_METRICS.includes(metric)) return "performance"
  return "performance" // default group
}

const CREATIVE_REPORTS_METRICS_OPTIONS = Object.keys(
  CREATIVE_REPORTS_METRICS_MAP,
).map(
  (key: string) =>
    ({
      label: CREATIVE_REPORTS_METRICS_MAP[key as TMetricsMapKey],
      value: key,
      group: getMetricGroup(key as TMetricsMapKey),
    }) as TMultiSelectWithChipsOption,
)

const CREATIVE_REPORTS_METRICS_OPTIONS_MAP = Object.keys(
  CREATIVE_REPORTS_METRICS_MAP,
).reduce(
  (acc: Record<TMetricsMapKey, TMultiSelectWithChipsOption>, key: string) => ({
    ...acc,
    [key]: {
      label: CREATIVE_REPORTS_METRICS_MAP[key as TMetricsMapKey],
      value: key,
      group: getMetricGroup(key as TMetricsMapKey),
    } as TMultiSelectWithChipsOption,
  }),
  {} as Record<TMetricsMapKey, TMultiSelectWithChipsOption>,
)

const dayjs = useDate()

const maxDateRestriction = dayjs().toDate()

const CHART_ID = "cr-v2"

const getMetricType = (
  metric: TMetricsMapKey,
): "currency" | "coef" | "percent" | "number" => {
  if (MONETARY_METRICS.includes(metric)) {
    return "currency"
  }
  if (COEF_METRICS.includes(metric)) {
    return "coef"
  }
  if (PERCENTAGE_METRICS.includes(metric)) {
    return "percent"
  }
  return "number"
}

export default function CreativeReportsV2Page() {
  const { params, setParams } = useURLSearchParams()
  const {
    urlKey: sortUrlKey,
    sorting,
    setSorting,
  } = useUrlSorting(CHART_ID, [
    {
      id: "brand_value_agg",
      desc: true,
    },
  ])
  const [, rerender] = useState(0)
  const { getSelectOptions, isLoading: isLoadingFilterOptions } =
    useSelectFilterOptions()
  const { openDrawer } = useDrawer()
  const dispatch = useAppDispatch()
  const cachedUrls = useAppSelector(state => state.assetCacheSlice.urls)
  const cachedAssets = useAppSelector(state => state.assetCacheSlice.assets)
  const [resolveAssetsMutation] = useResolveAssetsMutation()
  const [getMetaCreativeMedia] = useGetMetaCreativeMediaMutation()
  // Meta creatives are shown from Meta itself (live, signed fbcdn URLs that
  // expire), so they live here in memory rather than in the asset cache.
  const [metaMedia, setMetaMedia] = useState<
    Record<
      string,
      { cardUrl: string; fullUrl: string; mediaType?: "IMAGE" | "VIDEO" }
    >
  >({})
  const requestedMetaIdsRef = useRef(new Set<string>())
  // Creative ids whose Meta media request is in flight: their cards show a loader.
  const [pendingMetaIds, setPendingMetaIds] = useState<ReadonlySet<string>>(
    new Set(),
  )

  // Infinite scroll state
  const [currentPage, setCurrentPage] = useState(0)
  const [allItems, setAllItems] = useState<Array<Record<string, string>>>([])
  const [hasMore, setHasMore] = useState(true)
  const observerTarget = useRef<HTMLDivElement>(null)

  // Creative Blaster modal (Blast button on cards)
  const [blasterModalOpen, setBlasterModalOpen] = useState(false)
  const [blasterCreativeData, setBlasterCreativeData] = useState<{
    creativeId?: string
    objectStoryId?: string
    adId?: string
    imageUrl?: string
    destinationUrl?: string
    card: Record<string, string>
  } | null>(null)

  const creativeReportMode = useMemo<TCreativeReport>(
    () =>
      (params[MODE_URL_KEY] as TCreativeReport) ??
      CREATIVE_REPORT_OPTIONS[0].value,
    [params],
  )

  // Group-by-media-channel toggle only applies in Root Ad Name mode. Default is
  // OFF (aggregated): one card per root ad name, collapsing every channel/ad so
  // the report doesn't show a separate card per ad. Opt in via the toggle to see
  // per-channel cards.
  const isRootAdNameMode = creativeReportMode === CREATIVE_REPORT.ROOT_AD_NAME
  const groupByMediaChannel =
    !isRootAdNameMode || params[GROUP_BY_CHANNEL_URL_KEY] === "true"
  const isAggregated = !groupByMediaChannel

  // Get URL filters
  const campaignNameFilter = params["campaign_name"]
  const adsetNameFilter = params["adset_name"]

  const selectedMetrics: TMetricsMapKey[] = useMemo(() => {
    const metricsFromParams = params[METRICS_URL_KEY]
      ? (params[METRICS_URL_KEY]?.split(",") as TMetricsMapKey[])
      : []

    return metricsFromParams.length > 0
      ? [...new Set([...ALWAYS_METRICS, ...metricsFromParams])]
      : [...ALWAYS_METRICS, ...DEFAULT_METRICS]
  }, [params])

  const selectedMetricsOptions: TMultiSelectWithChipsOption[] = useMemo(
    () =>
      selectedMetrics.map(id => ({
        ...CREATIVE_REPORTS_METRICS_OPTIONS_MAP[id],
        permanent: ALWAYS_METRICS.includes(id),
      })),
    [selectedMetrics],
  )

  const { dateRange } = useMinMaxDateRange(
    undefined,
    maxDateRestriction,
    AggregationType.DAILY,
    false,
    undefined,
    "last7Days",
  )
  const { setPeriod } = usePeriod()

  const { urlKey: filtersUrlKey, columnFilters } = useUrlColumnFilters(CHART_ID)

  // Build the cube query
  const { cubeQuery, prefix, amazonEnabled, toggleAmazon } = useConnect(
    creativeReportMode,
    dateRange,
    currentPage,
    columnFilters,
    sorting[0],
    campaignNameFilter,
    adsetNameFilter,
    groupByMediaChannel,
  )

  // Execute the query with useCubeQuery
  const { resultSet, isLoading, error } = useCubeQuery(cubeQuery)

  // Track the last processed result to avoid duplicate processing
  const lastResultSetRef = useRef<typeof resultSet>(null)

  // Process result data and append to existing items
  useEffect(() => {
    if (!resultSet || resultSet === lastResultSetRef.current) return

    lastResultSetRef.current = resultSet

    const newItems = resultSet.rawData().map(item => {
      const noPrefixItm: Record<string, string> = {}
      Object.keys(item).forEach(keyWithPrefix => {
        const keyNoPrefix = keyWithPrefix.replace(`${prefix}.`, "")
        noPrefixItm[keyNoPrefix] = item[keyWithPrefix]
      })
      return noPrefixItm
    })

    const totalRows = resultSet.totalRows() ?? 0
    const totalPages = Math.ceil(totalRows / ITEMS_PER_PAGE)

    if (currentPage === 0) {
      // First page - replace all items
      setAllItems(newItems)
    } else {
      // Append to existing items
      setAllItems(prev => [...prev, ...newItems])
    }

    // Check if there are more pages
    setHasMore(currentPage + 1 < totalPages)
  }, [resultSet, prefix, currentPage])

  // Bulk resolve assets for creative images
  useEffect(() => {
    if (!allItems.length) return

    const uncached: Array<{
      sourceType: AssetSourceType
      externalRefId: string
      url?: string
    }> = []
    const seen = new Set<string>()

    for (const item of allItems) {
      // In aggregated mode the representative creative/channel/image come from
      // the highest-spend row (top_spend_* measures) rather than the per-row
      // dimensions, which aren't present when grouping only by ad name.
      const creativeId = isAggregated
        ? item.top_spend_creative_id
        : item.creative_id_agg
      const channel = isAggregated
        ? item.top_spend_media_channel
        : item.media_channel
      if (!creativeId || !channel) continue

      const sourceType = mapMediaChannelToSourceType(String(channel))
      const key = `${sourceType}:${creativeId}`
      if (seen.has(key)) continue
      if (sourceType === "FACEBOOK_AD") {
        if (requestedMetaIdsRef.current.has(String(creativeId))) continue
      } else if (cachedUrls[key]) {
        continue
      }
      seen.add(key)

      const url =
        (isAggregated ? item.top_spend_image_url : item.md_image_url) ||
        undefined
      uncached.push({
        sourceType,
        externalRefId: String(creativeId),
        ...(url && { url }),
      })
    }

    if (uncached.length === 0) return

    // First-ever creative ids come back PENDING_DOWNLOAD while the pipeline
    // downloads them (~20-30s typically), so re-resolve those on a short
    // schedule instead of showing placeholders until a manual reload.
    let cancelled = false
    const timers: Array<ReturnType<typeof setTimeout>> = []

    const resolve = (assets: typeof uncached, retryDelaysMs: number[]) => {
      void resolveAssetsMutation({ assets })
        .unwrap()
        .then(result => {
          const stillPending: typeof uncached = []
          for (const req of assets) {
            const key = `${req.sourceType}:${req.externalRefId}`
            const asset = result.results[key]
            if (!asset) continue

            // Cache ONLY the AMS-resolved URL for a downloaded (SYNCED) asset.
            // Previously this fell back to the md_image_url hint we passed in,
            // caching a guessed S3 URL that 404s; trust AMS and leave the rest
            // to render as a placeholder. Full info (incl. mediaType) is kept
            // so video assets render as playable video, not a still.
            if (asset.syncStatus === "SYNCED") {
              const resolvedUrl = asset.url || asset.thumbnailUrl || null
              if (resolvedUrl) {
                dispatch(
                  cacheAssetInfo({
                    key,
                    info: {
                      url: resolvedUrl,
                      thumbnailUrl: asset.thumbnailUrl ?? undefined,
                      // A VIDEO asset without a playable url fell back to its
                      // thumbnail — render it as an image, not a broken <video>.
                      mediaType: asset.url ? asset.mediaType : "IMAGE",
                      assetId: asset.id,
                    },
                  }),
                )
              }
            } else if (asset.syncStatus === "PENDING_DOWNLOAD") {
              // FAILED is terminal server-side — retrying it is pure noise.
              stillPending.push(req)
            }
          }

          const [delayMs, ...remainingDelays] = retryDelaysMs
          if (!cancelled && stillPending.length > 0 && delayMs !== undefined) {
            timers.push(
              setTimeout(() => resolve(stillPending, remainingDelays), delayMs),
            )
          }
        })
        .catch(() => {
          /* fall through — cards keep their placeholder */
        })
    }

    const retryDelaysMs = [20_000, 75_000]
    // Meta creatives load straight from Meta; AMS stays the source for the
    // other channels and the fallback for Meta ids Meta can't serve.
    const metaReqs = uncached.filter(req => req.sourceType === "FACEBOOK_AD")
    const otherReqs = uncached.filter(req => req.sourceType !== "FACEBOOK_AD")
    if (otherReqs.length > 0) resolve(otherReqs, retryDelaysMs)
    if (metaReqs.length > 0) {
      const metaIds = metaReqs.map(req => req.externalRefId)
      for (const id of metaIds) requestedMetaIdsRef.current.add(id)
      setPendingMetaIds(prev => new Set([...prev, ...metaIds]))
      // A fallback that lands after this effect re-ran (next page appended)
      // still resolves once, just without retries.
      const fallback = (assets: typeof uncached) =>
        resolve(assets, cancelled ? [] : retryDelaysMs)
      void getMetaCreativeMedia({ creativeIds: metaIds })
        .unwrap()
        .then(media => {
          const found: typeof metaMedia = {}
          const misses: typeof uncached = []
          for (const req of metaReqs) {
            const m = media[req.externalRefId]
            if (m?.videoUrl) {
              // Playable video: the card shows its first frame, the drawer plays it.
              found[req.externalRefId] = {
                cardUrl: m.videoUrl,
                fullUrl: m.videoUrl,
                mediaType: "VIDEO",
              }
              continue
            }
            // The sized thumbnail fills a card; the drawer gets full-res.
            const cardUrl = m?.thumbnailUrl ?? m?.imageUrl
            const fullUrl = m?.imageUrl ?? m?.thumbnailUrl
            if (cardUrl && fullUrl) {
              found[req.externalRefId] = { cardUrl, fullUrl }
            } else {
              misses.push(req)
            }
          }
          if (Object.keys(found).length > 0) {
            setMetaMedia(prev => ({ ...prev, ...found }))
          }
          if (misses.length > 0) fallback(misses)
        })
        .catch(() => fallback(metaReqs))
        .finally(() =>
          setPendingMetaIds(prev => {
            const next = new Set(prev)
            for (const id of metaIds) next.delete(id)
            return next
          }),
        )
    }

    return () => {
      cancelled = true
      for (const timer of timers) clearTimeout(timer)
    }
  }, [allItems])

  // Create stable keys for reset dependencies
  const resetKey = useMemo(
    () =>
      `${creativeReportMode}-${groupByMediaChannel}-${dateRange[0]?.getTime()}-${dateRange[1]?.getTime()}-${JSON.stringify(columnFilters)}-${JSON.stringify(sorting)}-${campaignNameFilter ?? ""}-${adsetNameFilter ?? ""}`,
    [
      creativeReportMode,
      groupByMediaChannel,
      dateRange,
      columnFilters,
      sorting,
      campaignNameFilter,
      adsetNameFilter,
    ],
  )

  // Reset when filters/sorting/date range changes
  useEffect(() => {
    setCurrentPage(0)
    setAllItems([])
    setHasMore(true)
    lastResultSetRef.current = null
  }, [resetKey])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (error) return

    const observer = new IntersectionObserver(
      entries => {
        // allItems.length guard: while the capabilities gate holds the query
        // back, isLoading is false with no items rendered, so the sentinel is
        // visible and the observer would advance to page 2 before page 1 ever
        // fired (the first request would go out with offset 12).
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          allItems.length > 0
        ) {
          setCurrentPage(prev => prev + 1)
        }
      },
      { threshold: 0.1 },
    )

    const currentTarget = observerTarget.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
    }
  }, [hasMore, isLoading, error, allItems.length])

  const setCreativeReportMode = (mode: TCreativeReport) => {
    setParams({
      [MODE_URL_KEY]: mode,
    })
  }

  const changePeriod = ({ start, end }: { start: Date; end: Date }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )
  }

  const setSelectedMetrics = (values: TMultiSelectWithChipsOption[]) => {
    const metrics: Array<string | null> = values.map(({ value }) => value)

    setParams({
      [METRICS_URL_KEY]: metrics.join(","),
    })
    rerender(idx => idx + 1)
  }

  // Metrics, sort and filters must go out in ONE setParams call: each of the
  // URL hooks (useUrlSorting, useUrlColumnFilters) wraps its own
  // useSearchParams instance, and consecutive functional setSearchParams calls
  // all start from the pre-click URL — the last navigation wins and silently
  // drops the earlier writes (e.g. the spend sort reverting to the default
  // brand value sort). Sort/filters are qs-encoded exactly like
  // useUrlParamsState encodes them so the hooks parse them back.
  const applyMetricsPreset = (preset: (typeof METRIC_PRESETS)[number]) => {
    const nextFilters = [
      ...(columnFilters ?? []).filter(({ id }) => !PRESET_FILTER_IDS.has(id)),
      ...(preset.defaultFilters ?? []),
    ]
    const qsOptions = {
      arrayFormat: "indices" as const,
      allowEmptyArrays: true,
    }
    setParams({
      [METRICS_URL_KEY]: preset.metrics.join(","),
      ...(preset.defaultSort && {
        [sortUrlKey]: qs.stringify([preset.defaultSort], qsOptions),
      }),
      [filtersUrlKey]: qs.stringify(nextFilters, qsOptions) || undefined,
    })
    rerender(idx => idx + 1)
  }

  // A preset is "active" when the current selection matches it exactly
  // (including the always-on metrics merged in).
  const activePresetLabel = useMemo(() => {
    const selectedSet = new Set(selectedMetrics)
    return METRIC_PRESETS.find(preset => {
      const presetSet = new Set([...ALWAYS_METRICS, ...preset.metrics])
      return (
        presetSet.size === selectedSet.size &&
        [...presetSet].every(metric => selectedSet.has(metric))
      )
    })?.label
  }, [selectedMetrics])

  const selectedCreativeReportModel = useMemo(
    () =>
      CREATIVE_REPORT_OPTIONS.find(
        option => option.value === creativeReportMode,
      ),
    [creativeReportMode],
  )

  const selectedCreativeReportModeLabel = useMemo(
    () => selectedCreativeReportModel?.label,
    [selectedCreativeReportModel],
  )

  const selectedCreativeReportModePrimaryId = useMemo(
    () => selectedCreativeReportModel?.primaryId,
    [selectedCreativeReportModel],
  )

  const selectedCreativeReportModeSkipTitle = useMemo(
    () => selectedCreativeReportModel?.skipPrimaryId,
    [selectedCreativeReportModel],
  )

  const alwaysOptions = useMemo(
    () => ALWAYS_METRICS.map(id => CREATIVE_REPORTS_METRICS_OPTIONS_MAP[id]),
    [],
  )
  const removableOptions = useMemo(
    () =>
      CREATIVE_REPORTS_METRICS_OPTIONS.filter(
        option => !ALWAYS_METRICS.includes(option.value as TMetricsMapKey),
      ),
    [selectedMetrics],
  )

  // Simplified column config for filters and sorting only (no table rendering)
  const columnConfig = useMemo(
    () =>
      selectedMetrics.map(metric => ({
        key: metric,
        header: CREATIVE_REPORTS_METRICS_MAP[metric],
        type: getMetricType(metric),
      })),
    [selectedMetrics],
  )

  const additionalFilters = useMemo(
    () =>
      [
        // Dimension filters
        {
          value: "all_product_handles_str",
          label: "Product",
          type: "searchable-select" as const,
        },
        {
          value: "media_channel",
          label: "Media Channel",
          type: "text" as const,
        },
        {
          value: "campaign_name",
          label: "Campaign Name",
          type: "text" as const,
        },
        {
          value: "adset_name",
          label: "Ad Set Name",
          type: "text" as const,
        },
        {
          value: "ad_name",
          label: "Ad Name",
          type: "text" as const,
        },
        {
          value: "initiative_type",
          label: "Initiative Type",
          type: "text" as const,
        },
        // Metric filters
        {
          value: "spend",
          label: "Total Spend",
          type: "range" as const,
        },
        {
          value: "brand_value_agg",
          label: "Brand Value",
          type: "range" as const,
        },
        {
          value: "total_value",
          label: "Total Value",
          type: "range" as const,
        },
        {
          value: "purchase_value",
          label: "Total Purchase Value",
          type: "range" as const,
        },
        {
          value: "purchases",
          label: "Total Purchases",
          type: "range" as const,
        },
        {
          value: "comments",
          label: "Total Comments",
          type: "range" as const,
        },
        {
          value: "likes",
          label: "Total Likes",
          type: "range" as const,
        },
        {
          value: "shares",
          label: "Total Shares",
          type: "range" as const,
        },
        {
          value: "follows",
          label: "Total Follows",
          type: "range" as const,
        },
        {
          value: "saves",
          label: "Total Saves",
          type: "range" as const,
        },
      ] as TManualFilter[],
    [],
  )

  const selectOptions = useMemo(
    () => [
      ...alwaysOptions.map(option => ({
        ...option,
        permanent: true,
      })),
      ...removableOptions,
    ],
    [alwaysOptions, removableOptions],
  )

  const handleSort = (sort: TSortingState) => {
    if (
      Object.prototype.hasOwnProperty.call(sort, "desc") &&
      Object.prototype.hasOwnProperty.call(sort, "id")
    ) {
      setSorting([sort])
    }
  }

  const handleOpenDrawer = ({
    name,
    type,
    card,
  }: {
    name: string
    type: "campaign" | "adset" | "ad"
    card: Record<string, string>
  }) => {
    // Disable drawers for Campaign and Ad Set levels in Creative reports
    // Only allow Ad-level drawer to open
    if (type !== "ad") {
      return
    }

    // At this point, TypeScript knows type === "ad"
    const cubePrefix = "company_media_metrics_ads"
    const nameField = `${cubePrefix}.ad_name`

    // Build cube filters to filter by name (not ID)
    const cubeFilters = {
      and: [
        {
          member: nameField,
          operator: "equals" as const,
          values: [name],
        },
        ...(card.media_channel
          ? [
              {
                member: `${cubePrefix}.media_channel`,
                operator: "equals" as const,
                values: [card.media_channel],
              },
            ]
          : []),
      ],
    }

    openDrawer({
      clickSourceId: ".creative-report-card",
      type: DRAWER_TYPE.CampaingAdSetAdDetails,
      props: {
        metric: {
          ...card,
          ad_name: name,
        },
        queryPrefix: cubePrefix,
        cubeFilters,
        component: "AdDetails",
        widget: CHART_ID,
        dateRange: [
          dayjs(dateRange[0]).format(DATE_FORMAT),
          dayjs(dateRange[1]).format(DATE_FORMAT),
        ] as [string, string],
        hideViewAdsets: true, // Hide navigation buttons (Ad is lowest level)
      },
    } as TDrawerConfig)
  }

  // Card click → creative details drawer (image, daily trend, all metrics,
  // serving campaigns/ad sets), scoped to the current report mode's primary id.
  const handleCardClick = (
    card: Record<string, string>,
    imgUrl?: string,
    assetMediaType?: "IMAGE" | "VIDEO",
  ) => {
    const primaryIdKey = selectedCreativeReportModePrimaryId ?? "root_ad_name"
    const title = card[primaryIdKey] || card.root_ad_name || "Creative"

    openDrawer({
      clickSourceId: ".creative-report-card",
      type: DRAWER_TYPE.CampaingAdSetAdDetails,
      props: {
        component: "CreativeDetails",
        metric: card,
        title,
        primaryIdKey,
        primaryIdLabel: selectedCreativeReportModeLabel,
        isAggregated,
        imgUrl,
        assetMediaType,
        metricsNames: selectedMetrics,
        widget: CHART_ID,
        dateRange: [
          dayjs(dateRange[0]).format(DATE_FORMAT),
          dayjs(dateRange[1]).format(DATE_FORMAT),
        ] as [string, string],
      },
    } as TDrawerConfig)
  }

  const handleBlastClick = (
    creativeData: NonNullable<typeof blasterCreativeData>,
  ) => {
    setBlasterCreativeData(creativeData)
    setBlasterModalOpen(true)
  }

  const handleBlasterModalClose = () => {
    setBlasterModalOpen(false)
    setBlasterCreativeData(null)
  }

  return (
    <div className="creative-reports-page h-full overflow-y-visible px-system-md">
      <Breadcrumbs
        slug={brandRoutes.CREATIVE_REPORTS.V2}
        title={"Creative Reporting v2"}
        parent={{
          slug: brandRoutes.CREATIVE_REPORTS.V2,
          title: "Creative Reports v2",
        }}
      />
      <div className="mt-6 flex justify-between items-center gap-system-sm">
        <div className="flex items-stretch gap-system-sm flex-shrink-0">
          <DropdownMenu
            options={CREATIVE_REPORT_OPTIONS}
            onItemClick={value =>
              setCreativeReportMode(value as TCreativeReport)
            }
            className="cursor-pointer bg-system-blue-700 border border-system-blue-600 text-base rounded-system-md"
          >
            <div className="flex gap-2 items-center justify-center">
              <ViewfinderCircleIcon className="w-5 h-5 text-white"></ViewfinderCircleIcon>
              <div>Report on: {selectedCreativeReportModeLabel}</div>
              <ChevronDownIcon className="w-system-md h-system-md text-white" />
            </div>
          </DropdownMenu>

          {/* Active filters indicator */}
          {(campaignNameFilter || adsetNameFilter) && (
            <div className="flex items-center gap-2 px-4 py-2 bg-brand/20 border border-brand rounded-system-md text-sm">
              <span className="text-white font-medium">Filtered by:</span>
              {campaignNameFilter && (
                <span className="px-2 py-1 bg-brand rounded text-white text-xs">
                  Campaign: {campaignNameFilter}
                </span>
              )}
              {adsetNameFilter && (
                <span className="px-2 py-1 bg-brand rounded text-white text-xs">
                  Ad Set: {adsetNameFilter}
                </span>
              )}
              <button
                onClick={() => {
                  setParams({
                    campaign_name: undefined,
                    adset_name: undefined,
                  })
                }}
                className="ml-2 text-white hover:text-brand transition-colors"
                title="Clear filters"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        <div className="flex items-stretch gap-system-sm flex-shrink-0">
          <TableFiltersBar
            id={CHART_ID}
            columnConfig={columnConfig}
            additionalFilters={additionalFilters}
            getSelectOptions={getSelectOptions}
            isLoadingOptions={isLoadingFilterOptions}
          />
          <SortByControl
            columnConfig={columnConfig}
            sortBy={sorting[0]}
            onSort={handleSort}
          />
          {isRootAdNameMode && (
            <div className="flex items-center gap-system-xs px-system-sm py-system-xxs text-sm rounded-system-md border border-system-blue-600 bg-system-blue-700 h-system-2xl">
              <RichTooltip
                content={
                  <Card rounded className="p-0 bg-transparent border-none">
                    When on, results are grouped by ad name and media channel.
                    Turn off to aggregate metrics across all channels per ad
                    name.
                  </Card>
                }
              >
                <span className="text-white whitespace-nowrap">
                  Group by Media Channel
                </span>
              </RichTooltip>
              <Toggle
                size="xs"
                value={groupByMediaChannel}
                onChange={value =>
                  setParams({
                    [GROUP_BY_CHANNEL_URL_KEY]: value ? "true" : undefined,
                  })
                }
              />
            </div>
          )}
          {toggleAmazon && (
            <AmazonToggle
              amazonEnabled={amazonEnabled}
              toggleAmazon={toggleAmazon}
            />
          )}
          {dateRange?.[0] &&
            dateRange?.[1] &&
            isValidDate(dateRange[0]) &&
            isValidDate(dateRange[1]) && (
              <DateRangePicker
                start={dateRange[0]}
                end={dateRange[1]}
                comparisonEnabled={false}
                maxDate={maxDateRestriction}
                onChange={changePeriod}
              />
            )}
        </div>
      </div>
      <div className="flex items-stretch w-full mt-system-sm gap-system-sm">
        <div className="flex items-center flex-shrink-0">
          <DropdownMenu
            options={METRIC_PRESETS.map(({ label }) => ({
              label,
              value: label,
            }))}
            onItemClick={value => {
              const preset = METRIC_PRESETS.find(({ label }) => label === value)
              if (preset) {
                applyMetricsPreset(preset)
              }
            }}
            className="cursor-pointer bg-system-blue-700 border border-system-blue-600 text-base rounded-system-md"
          >
            <div className="flex gap-2 items-center justify-center">
              <BoltIcon className="w-5 h-5 text-white" />
              <div>Preset: {activePresetLabel ?? "Custom"}</div>
              <ChevronDownIcon className="w-system-md h-system-md text-white" />
            </div>
          </DropdownMenu>
        </div>
        <MultiSelectWithChips
          label="Columns"
          options={selectOptions}
          className="h-system-2xl cursor-pointer"
          groups={METRIC_GROUPS}
          showTypeAhead={true}
          value={selectedMetricsOptions}
          onChange={options => setSelectedMetrics(options)}
        />
      </div>

      {/* Error display */}
      {error && (
        <div className="mt-6 p-4 bg-error/10 border border-error rounded-lg">
          <div className="flex items-center gap-2 text-error">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <div className="font-semibold">Failed to load data</div>
              <div className="text-sm mt-1">
                {error.message || "An unexpected error occurred"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show big loader only on initial load with no items */}
      {allItems.length === 0 && isLoading ? (
        <div className="h-96 w-full flex items-center justify-center">
          <Loader size={50} />
        </div>
      ) : (
        <>
          {/* Grid of cards */}
          <div className="mt-system-xl grid grid-cols-4 gap-system-sm relative z-10">
            {allItems.map((item, idx) => {
              const itemPrimaryId =
                item[selectedCreativeReportModePrimaryId ?? ""]
              const creativeId = isAggregated
                ? item.top_spend_creative_id
                : item.creative_id_agg
              const channel = isAggregated
                ? item.top_spend_media_channel
                : item.media_channel
              const cacheKey =
                creativeId && channel
                  ? `${mapMediaChannelToSourceType(channel)}:${creativeId}`
                  : null
              // Only the AMS-resolved (SYNCED) URL is trustworthy. The cube's
              // md_image_url / top_spend_image_url are guessed S3 paths that 404,
              // so an unresolved asset shows a placeholder rather than a broken
              // image.
              const assetInfo = cacheKey ? cachedAssets[cacheKey] : undefined
              // Meta creatives: Meta's own image first, AMS only as fallback.
              const live =
                cacheKey?.startsWith("FACEBOOK_AD:") && creativeId
                  ? metaMedia[String(creativeId)]
                  : undefined
              const imgUrl =
                live?.cardUrl ??
                assetInfo?.url ??
                (cacheKey ? cachedUrls[cacheKey] : undefined)
              const mediaType = live ? live.mediaType : assetInfo?.mediaType
              return (
                <CreativeReportCard
                  key={`${itemPrimaryId}_${idx}`}
                  imgUrl={imgUrl}
                  assetMediaType={mediaType}
                  isMediaLoading={
                    !!creativeId &&
                    cacheKey?.startsWith("FACEBOOK_AD:") &&
                    pendingMetaIds.has(String(creativeId))
                  }
                  primaryIdKey={
                    selectedCreativeReportModePrimaryId ?? "root_ad_name"
                  }
                  skipTitle={selectedCreativeReportModeSkipTitle}
                  card={item}
                  isAggregated={isAggregated}
                  metricsNames={selectedMetrics}
                  onOpenDrawer={handleOpenDrawer}
                  disabledPillTypes={[]}
                  filterPillTypes={["campaign", "adset"]}
                  onFilterByName={({ name, type }) => {
                    if (type === "campaign") {
                      setParams({ campaign_name: name })
                    } else if (type === "adset") {
                      setParams({ adset_name: name })
                    }
                  }}
                  onBlastClick={handleBlastClick}
                  onCardClick={clickedCard =>
                    handleCardClick(
                      clickedCard,
                      live?.fullUrl ?? imgUrl,
                      mediaType,
                    )
                  }
                />
              )
            })}
          </div>

          {/* Infinite scroll trigger and loading indicator */}
          {hasMore && !error && (
            <div
              ref={observerTarget}
              className="mt-6 flex items-center justify-center py-8"
            >
              {isLoading ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader size={24} />
                  <span>Loading more...</span>
                </div>
              ) : (
                <div className="h-1" />
              )}
            </div>
          )}

          {/* End of results message */}
          {!hasMore && allItems.length > 0 && (
            <div className="mt-6 text-center text-gray-500 py-8">
              No more items to load
            </div>
          )}

          {/* No results message */}
          {!isLoading && allItems.length === 0 && (
            <div className="mt-12 text-center text-gray-500 py-8">
              No items found
            </div>
          )}
        </>
      )}

      {/* Creative Blaster Wizard */}
      {blasterCreativeData && (
        <BlastWizard
          isOpen={blasterModalOpen}
          onClose={handleBlasterModalClose}
          source={
            {
              type: "creative",
              platform: "meta",
              creativeId: blasterCreativeData.creativeId,
              objectStoryId: blasterCreativeData.objectStoryId,
              adId: blasterCreativeData.adId,
              name: blasterCreativeData.card?.root_ad_name || "Creative",
              imageUrl: blasterCreativeData.imageUrl,
              mediaChannel:
                blasterCreativeData.card?.media_channel ??
                (isAggregated ? "meta" : undefined),
              sourcePreference: blasterCreativeData.objectStoryId
                ? "object_story_id"
                : "auto",
              defaultOverrides: {
                ...(blasterCreativeData.destinationUrl && {
                  destinationUrl: blasterCreativeData.destinationUrl,
                }),
                ...(blasterCreativeData.card?.headline && {
                  headline: blasterCreativeData.card.headline,
                }),
                ...(blasterCreativeData.card?.primary_text && {
                  primaryText: blasterCreativeData.card.primary_text,
                }),
                ...(blasterCreativeData.card?.description && {
                  description: blasterCreativeData.card.description,
                }),
              },
            } satisfies BlastSource
          }
        />
      )}
    </div>
  )
}
